import React, { useState } from "react";
import type {
  Step,
  HubSpotProduct,
  ItemImage,
  ShowWhen,
  ExclusionCondition,
  StepTranslations,
  TranslationLocale,
  TranslatableField,
} from "../../types/configurator";
import { TRANSLATION_LOCALES, TRANSLATABLE_FIELDS } from "../../types/configurator";
import { GripIcon, ChevronIcon, TrashIcon } from "../ui/Icons";
import { ConditionRow } from "./ConditionRow";
import { ExclusionRow } from "./ExclusionRow";
import { ProductDropZone } from "./ProductDropZone";

const LOCALE_LABELS: Record<TranslationLocale, string> = {
  en: "English",
  de: "Deutsch",
  fr: "Français",
  pt: "Português",
};

const FIELD_LABELS: Record<TranslatableField, string> = {
  title: "Title",
  description: "Description",
  breadcrumb: "Breadcrumb",
  proTip: "Pro Tip",
};

function isLocaleComplete(entry: StepTranslations[TranslationLocale] | undefined): boolean {
  if (!entry) return false;
  return TRANSLATABLE_FIELDS.every((f) => typeof entry[f] === "string" && entry[f]!.trim() !== "");
}

interface Props {
  step: Step;
  index: number;
  isExpanded: boolean;
  isDragOver: boolean;
  priorSteps: Step[];
  productMap: Record<string, HubSpotProduct>;
  itemImageMap: Record<string, ItemImage[]>;
  allSteps: Step[];
  onUpdate: (id: string, updates: Partial<Step>) => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onRemoveProduct: (stepId: string, itemUid: string) => void;
  onSetConditions: (stepId: string, conditions: ShowWhen[]) => void;
  onSetExclusions: (stepId: string, exclusions: ExclusionCondition[]) => void;
  onItemImageUpload: (itemKey: string, file: File) => Promise<void>;
  onItemImageDelete: (imageId: string, itemKey: string) => Promise<void>;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, stepId: string) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent, stepId: string) => void;
  onDragOver: (e: React.DragEvent, stepId: string) => void;
  onDragLeave: () => void;
  getStepIndex: (id: string) => number;
  /** True when more than one step in the flow has `calculatePricing` set. */
  pricingConflict?: boolean;
}

export const StepCard: React.FC<Props> = ({
  step,
  index,
  isExpanded,
  isDragOver,
  isDragging,
  priorSteps,
  productMap,
  itemImageMap,
  allSteps,
  onUpdate,
  onDuplicate,
  onRemove,
  onToggleExpand,
  onRemoveProduct,
  onSetConditions,
  onSetExclusions,
  onItemImageUpload,
  onItemImageDelete,
  onDragStart,
  onDragEnd,
  onDrop,
  onDragOver,
  onDragLeave,
  getStepIndex,
  pricingConflict = false,
}) => {
  const conditions = step.conditions ?? [];
  const exclusions = step.exclusions ?? [];

  const [translationsOpen, setTranslationsOpen] = useState(false);
  const [activeLocale, setActiveLocale] = useState<TranslationLocale>("en");
  const translations = step.translations ?? {};
  const completeLocaleCount = TRANSLATION_LOCALES.reduce(
    (n, loc) => n + (isLocaleComplete(translations[loc]) ? 1 : 0),
    0,
  );
  const anyTranslated = TRANSLATION_LOCALES.some((loc) => {
    const entry = translations[loc];
    return entry && TRANSLATABLE_FIELDS.some((f) => (entry[f] ?? "").trim() !== "");
  });

  const updateTranslation = (locale: TranslationLocale, field: TranslatableField, value: string) => {
    const nextLocaleEntry = { ...(translations[locale] ?? {}) };
    if (value.trim() === "") {
      delete nextLocaleEntry[field];
    } else {
      nextLocaleEntry[field] = value;
    }
    const nextAll: StepTranslations = { ...translations };
    if (Object.keys(nextLocaleEntry).length === 0) {
      delete nextAll[locale];
    } else {
      nextAll[locale] = nextLocaleEntry;
    }
    onUpdate(step.id, { translations: Object.keys(nextAll).length > 0 ? nextAll : undefined });
  };

  const addCondition = (condStepId: string) => {
    if (!condStepId) return;
    onSetConditions(step.id, [...conditions, { stepId: condStepId, selectedAny: [] }]);
  };

  const updateCondition = (idx: number, selectedAny: string[]) => {
    const next = conditions.map((c, i) => i === idx ? { ...c, selectedAny } : c);
    onSetConditions(step.id, next);
  };

  const removeCondition = (idx: number) => {
    onSetConditions(step.id, conditions.filter((_, i) => i !== idx));
  };

  const usedStepIds = new Set(conditions.map((c) => c.stepId));
  const availablePriorSteps = priorSteps.filter((ps) => !usedStepIds.has(ps.id));

  const addExclusion = (condStepId: string) => {
    if (!condStepId) return;
    onSetExclusions(step.id, [...exclusions, { stepId: condStepId, selectedAny: [], excludeItems: [] }]);
  };

  const updateExclusion = (idx: number, updated: ExclusionCondition) => {
    onSetExclusions(step.id, exclusions.map((e, i) => i === idx ? updated : e));
  };

  const removeExclusion = (idx: number) => {
    onSetExclusions(step.id, exclusions.filter((_, i) => i !== idx));
  };

  return (
    <div
      className={`step-card ${isExpanded ? "expanded" : ""} ${isDragOver ? "dragging-over" : ""} ${conditions.length > 0 ? "conditional" : ""} ${exclusions.length > 0 ? "has-exclusions" : ""}`}
      onDragOver={!isExpanded ? (e) => onDragOver(e, step.id) : undefined}
      onDrop={!isExpanded ? (e) => onDrop(e, step.id) : undefined}
      onDragLeave={!isExpanded ? onDragLeave : undefined}
    >
      <div
        className="step-header"
        draggable
        onDragStart={(e) => onDragStart(e, step.id)}
        onDragEnd={onDragEnd}
        onClick={() => onToggleExpand(step.id)}
      >
        <span style={{ color: "var(--text-muted)", cursor: "grab" }} onClick={(e) => e.stopPropagation()}>
          <GripIcon />
        </span>
        <div className="step-number">{index + 1}</div>
        <input
          className="step-title-input"
          value={step.title}
          onChange={(e) => onUpdate(step.id, { title: e.target.value })}
          placeholder={`Step ${index + 1} title...`}
          onClick={(e) => e.stopPropagation()}
        />
        <div className="step-badges">
          {step.selectionType === "multi" ? (
            <span className="badge badge-multi">Multi</span>
          ) : (
            <span className="badge badge-single">Single</span>
          )}
          {conditions.length > 0 && (
            <span className="badge badge-conditional">
              ↳ {conditions.map((c) => getStepIndex(c.stepId) + 1).join(' · ')}
            </span>
          )}
          {step.calculatePricing && (
            <span className={`badge badge-pricing${pricingConflict ? " badge-pricing-conflict" : ""}`}>
              Pricing
            </span>
          )}
          {step.hidePricing && <span className="badge badge-hidden-price">No prices</span>}
          {exclusions.length > 0 && (
            <span className="badge badge-exclusion">
              ⊘ {exclusions.map((e) => getStepIndex(e.stepId) + 1).join(' · ')}
            </span>
          )}
          {!(step.required ?? true) && <span className="badge badge-optional">Optional</span>}
          {anyTranslated && (
            <span
              className={`badge badge-translations${completeLocaleCount === TRANSLATION_LOCALES.length ? " badge-translations-complete" : ""}`}
              title={`${completeLocaleCount} of ${TRANSLATION_LOCALES.length} locales fully translated`}
            >
              🌐 {completeLocaleCount}/{TRANSLATION_LOCALES.length}
            </span>
          )}
        </div>
        <span className="step-item-count">{step.items.length} items</span>
        <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onToggleExpand(step.id); }}>
          <ChevronIcon open={isExpanded} />
        </button>
        <button className="btn btn-ghost btn-sm" title="Duplicate step" onClick={(e) => { e.stopPropagation(); onDuplicate(step.id); }}>
          ⧉
        </button>
        <button className="btn btn-ghost btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); onRemove(step.id); }}>
          <TrashIcon />
        </button>
      </div>

      {!isExpanded && (
        <div className={`collapsed-drop-zone${isDragging ? ' visible' : ''}${isDragOver ? ' drag-over' : ''}`}>
          <span>Drop here to add to this step</span>
        </div>
      )}

      {isExpanded && (
        <div className="step-body">
          {/* Settings */}
          <div className="step-settings">
            <div className="setting-group">
              <label>Selection Type</label>
              <select
                value={step.selectionType}
                onChange={(e) => onUpdate(step.id, { selectionType: e.target.value as any })}
              >
                <option value="single">Single Select</option>
                <option value="multi">Multi Select</option>
              </select>
            </div>
            <div className="setting-group">
              <label>Required</label>
              <select
                value={(step.required ?? true) ? "true" : "false"}
                onChange={(e) => onUpdate(step.id, { required: e.target.value === "true" })}
              >
                <option value="true">Required</option>
                <option value="false">Optional</option>
              </select>
            </div>
            <div className="setting-group setting-group--grow">
              <label>Breadcrumb Title</label>
              <input
                type="text"
                value={step.breadcrumb}
                onChange={(e) => onUpdate(step.id, { breadcrumb: e.target.value })}
                placeholder="Short label for breadcrumb navigation..."
              />
            </div>
            <div className="setting-group setting-group--grow">
              <label>Pro Tip</label>
              <input
                type="text"
                value={step.proTip}
                onChange={(e) => onUpdate(step.id, { proTip: e.target.value })}
                placeholder="Optional tip shown to the customer..."
              />
            </div>
            <div className="setting-group setting-group--grow">
              <label>Description</label>
              <input
                type="text"
                value={step.description}
                onChange={(e) => onUpdate(step.id, { description: e.target.value })}
                placeholder="Description (optional)..."
              />
            </div>
            <div className="setting-group setting-group--narrow">
              <label>Priority</label>
              <input
                type="number"
                value={step.priority ?? 0}
                onChange={(e) => onUpdate(step.id, { priority: Number(e.target.value) })}
              />
              <span className="setting-hint">
                Order in the quote summary &amp; PDF: 1 shows first, then 2, 3&hellip;. Leave at 0 to keep its natural order below prioritised steps. Does not change the step order customers walk through.
              </span>
            </div>
            <div className="setting-group setting-group--narrow">
              <label>
                <input
                  type="checkbox"
                  checked={step.calculatePricing ?? false}
                  onChange={() => onUpdate(step.id, { calculatePricing: !step.calculatePricing })}
                />
                {" "}Calculate pricing at this step
              </label>
              <span className="setting-hint">
                The configurator will call the pricing API with all selections made up to this step.
                Only one step per flow may have this enabled.
              </span>
            </div>
            <div className="setting-group setting-group--narrow">
              <label>
                <input
                  type="checkbox"
                  checked={step.hidePricing ?? false}
                  onChange={() => onUpdate(step.id, { hidePricing: !step.hidePricing })}
                />
                {" "}Hide prices in this step
              </label>
              <span className="setting-hint">
                Customers see no price on any item in this step, and none in the quote summary at
                the end of the flow. The prices are still used to calculate the offer.
              </span>
            </div>
          </div>

          {/* Translations */}
          <div className={`translations-section${translationsOpen ? " open" : ""}`}>
            <div
              className="translations-header"
              onClick={() => setTranslationsOpen((o) => !o)}
              role="button"
              tabIndex={0}
            >
              <span className="translations-label">Translations</span>
              <span className="translations-count">
                {completeLocaleCount} of {TRANSLATION_LOCALES.length} translated
              </span>
              <span className="translations-chevron">
                <ChevronIcon open={translationsOpen} />
              </span>
            </div>
            {translationsOpen && (
              <div className="translations-body">
                <div className="translations-tabs">
                  {TRANSLATION_LOCALES.map((loc) => {
                    const complete = isLocaleComplete(translations[loc]);
                    const hasAny =
                      translations[loc] &&
                      TRANSLATABLE_FIELDS.some((f) => (translations[loc]![f] ?? "").trim() !== "");
                    return (
                      <button
                        key={loc}
                        type="button"
                        className={`translations-tab${activeLocale === loc ? " active" : ""}${complete ? " complete" : hasAny ? " partial" : ""}`}
                        onClick={() => setActiveLocale(loc)}
                      >
                        {LOCALE_LABELS[loc]}
                        {complete ? " ✓" : hasAny ? " •" : ""}
                      </button>
                    );
                  })}
                </div>
                <div className="translations-fields">
                  {TRANSLATABLE_FIELDS.map((field) => {
                    const dutchValue = step[field] ?? "";
                    const translatedValue = translations[activeLocale]?.[field] ?? "";
                    return (
                      <div key={field} className="setting-group setting-group--grow">
                        <label>{FIELD_LABELS[field]}</label>
                        <input
                          type="text"
                          value={translatedValue}
                          onChange={(e) => updateTranslation(activeLocale, field, e.target.value)}
                          placeholder={dutchValue ? `NL: ${dutchValue}` : "(no Dutch value set)"}
                        />
                      </div>
                    );
                  })}
                </div>
                <p className="translations-hint">
                  Empty fields fall back to the Dutch value. Dutch remains the default and is edited above.
                </p>
              </div>
            )}
          </div>

          {/* Visibility conditions */}
          <div className="conditions-section">
            <div className="conditions-header">
              <span className="conditions-label">Visibility conditions</span>
              {availablePriorSteps.length > 0 && (
                <select
                  className="conditions-add-select"
                  value=""
                  onChange={(e) => { addCondition(e.target.value); e.target.value = ""; }}
                >
                  <option value="">+ Add condition</option>
                  {availablePriorSteps.map((ps) => {
                    const pi = getStepIndex(ps.id);
                    return (
                      <option key={ps.id} value={ps.id}>
                        Step {pi + 1}: {ps.title || `Step ${pi + 1}`}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
            {conditions.length === 0 ? (
              <p className="conditions-empty">Always visible — no conditions set</p>
            ) : (
              <div className="conditions-list">
                {conditions.map((cond, idx) => {
                  const condStep = allSteps.find((s) => s.id === cond.stepId);
                  if (!condStep) return null;
                  return (
                    <ConditionRow
                      key={cond.stepId}
                      condition={cond}
                      condStep={condStep}
                      condStepIndex={getStepIndex(cond.stepId)}
                      productMap={productMap}
                      onUpdate={(selectedAny) => updateCondition(idx, selectedAny)}
                      onRemove={() => removeCondition(idx)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Item exclusions */}
          <div className="exclusions-section">
            <div className="conditions-header">
              <span className="exclusions-label">Item exclusions</span>
              {priorSteps.length > 0 && (
                <select
                  className="conditions-add-select"
                  value=""
                  onChange={(e) => { addExclusion(e.target.value); e.target.value = ""; }}
                >
                  <option value="">+ Add exclusion</option>
                  {priorSteps.map((ps) => {
                    const pi = getStepIndex(ps.id);
                    return (
                      <option key={ps.id} value={ps.id}>
                        Step {pi + 1}: {ps.title || `Step ${pi + 1}`}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
            {exclusions.length === 0 ? (
              <p className="conditions-empty">No exclusions — all items always shown</p>
            ) : (
              <div className="conditions-list">
                {exclusions.map((excl, idx) => {
                  const condStep = allSteps.find((s) => s.id === excl.stepId);
                  if (!condStep) return null;
                  return (
                    <ExclusionRow
                      key={idx}
                      exclusion={excl}
                      condStep={condStep}
                      condStepIndex={getStepIndex(excl.stepId)}
                      thisStep={step}
                      productMap={productMap}
                      onUpdate={(updated) => updateExclusion(idx, updated)}
                      onRemove={() => removeExclusion(idx)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {/* Product drop zone */}
          <ProductDropZone
            step={step}
            isDragOver={isDragOver}
            productMap={productMap}
            itemImageMap={itemImageMap}
            onRemoveProduct={onRemoveProduct}
            onItemImageUpload={onItemImageUpload}
            onItemImageDelete={onItemImageDelete}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
          />
        </div>
      )}
    </div>
  );
};
